import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Flex } from '@kognitos/lattice';

import type { TRainValueJs } from '../utils/value';
import { RainValueViewer } from './RainValueViewer';
import type { TRainToolCallStatus } from './RainToolCallCard';

interface ISectionProps {
  title: string;
  children: ReactNode;
}

/**
 * A labelled payload block.
 *
 * The heading is a quiet mono label rather than an uppercase tracked one: two
 * of these stack inside a chat row, and shouting "ARGUMENTS" at the reader
 * competed with the tool's own title directly above it.
 *
 * Indented to the value rows' TEXT column (past their chevron gutter) so the
 * label and the keys under it share a left edge — flush-left, the heading hung
 * outside its own content.
 */
function Section({ title, children }: ISectionProps) {
  return (
    <Flex direction="column" gap={0.5} width="full" className="min-w-0">
      <span className="shrink-0 pl-[22px] font-mono text-[11px] text-muted-foreground/60">
        {title}
      </span>
      {children}
    </Flex>
  );
}

export interface IRainToolCallDetailsProps {
  status: TRainToolCallStatus;
  args: Record<string, TRainValueJs>;
  result?: TRainValueJs;
  errorMessage?: string;
  /** Whether the agent loop is paused on this call awaiting the user. */
  awaitingApproval?: boolean;
  className?: string;
}

/**
 * A tool call's arguments and result, stacked.
 *
 * STACKED rather than side by side, deliberately. Rain's own UI puts the two in
 * a symmetric grid, which works at its full-window width; the agent builder
 * gives this chat 40% of the split once the config pane enters Edit mode, and
 * two columns of value rows are unreadable there.
 *
 * Shared between the generic tool-call card and the raw escape hatch on the
 * bespoke cards, so a payload reads identically wherever it is reached from.
 */
export function RainToolCallDetails({
  status,
  args,
  result,
  errorMessage,
  awaitingApproval = false,
  className,
}: IRainToolCallDetailsProps) {
  const { t } = useTranslation('rain');

  const hasArgs = Object.keys(args).length > 0;
  const isError = status === 'error';
  const isRunning = status === 'running';

  return (
    <Flex
      direction="column"
      gap={3}
      width="full"
      className={cn('min-w-0', className)}
    >
      <Section title={t('rain.toolCall.arguments')}>
        {hasArgs ? (
          <RainValueViewer value={args} flattenRoot />
        ) : (
          <span className="pl-[22px] text-xs italic text-muted-foreground/70">
            {t('rain.toolCall.noArguments')}
          </span>
        )}
      </Section>

      <Section title={t('rain.toolCall.result')}>
        {isError ? (
          <pre
            className="min-w-0 max-w-full overflow-x-auto whitespace-pre-wrap break-words pl-[22px] font-mono text-xs text-warning"
            data-testid="rain-tool-call-error"
          >
            {errorMessage ?? t('rain.toolCall.unknownError')}
          </pre>
        ) : result !== undefined ? (
          // Flattened for the same reason as the arguments: a result that is an
          // object is the common case, and the section already says "result".
          // A scalar or list result is unaffected — `flattenRoot` only unwraps a
          // top-level container.
          <RainValueViewer value={result} flattenRoot />
        ) : (
          <span
            className="pl-[22px] text-xs italic text-muted-foreground/70"
            data-testid="rain-tool-call-pending-result"
          >
            {/* Arguments stream in over their own events, so a running call has
                a growing arguments section and nothing here yet — saying so is
                what distinguishes "still working" from "returned nothing". */}
            {awaitingApproval
              ? t('rain.toolCall.awaitingApprovalResult')
              : isRunning
                ? t('rain.toolCall.waitingResult')
                : t('rain.toolCall.noResult')}
          </span>
        )}
      </Section>
    </Flex>
  );
}
